import type { ExperiencePhoto } from '../types/experience';
import type { GalleryLayout } from '../types/gallery-layout';

type PreviewImageSource = 'desktop' | 'mobile';

const IMAGE_DIMENSIONS_REGEXP = /\/(?:fit-in\/)?(\d+)x(\d+)\//;
const PICSUM_DIMENSIONS_REGEXP = /\/(\d+)\/(\d+)(?:\?|$)/;

function extractDimensionsFromUrl(url?: string): { width: number; height: number } | null {
  if (!url) {
    return null;
  }

  const match = url.match(IMAGE_DIMENSIONS_REGEXP) ?? url.match(PICSUM_DIMENSIONS_REGEXP);
  if (match === null) {
    return null;
  }

  const width = Number.parseInt(match[1] ?? '', 10);
  const height = Number.parseInt(match[2] ?? '', 10);

  if (isNaN(width) || isNaN(height) || width <= 0 || height <= 0) {
    return null;
  }

  return { width, height };
}

export function getPhotoAspectRatio(photo: ExperiencePhoto): number | null {
  const dimensions = extractDimensionsFromUrl(photo.medium || photo.thumbnail);
  if (dimensions === null) {
    return null;
  }

  return dimensions.width / dimensions.height;
}

export function getPreviewImageSrc(photo: ExperiencePhoto, source: PreviewImageSource): string {
  if (source === 'mobile') {
    return photo.thumbnail || photo.medium || '';
  }

  return photo.medium || photo.thumbnail || '';
}

export function getPreviewImageWidth(photo: ExperiencePhoto, source: PreviewImageSource): number | undefined {
  return extractDimensionsFromUrl(getPreviewImageSrc(photo, source))?.width;
}

export function getPreviewImageHeight(photo: ExperiencePhoto, source: PreviewImageSource): number | undefined {
  return extractDimensionsFromUrl(getPreviewImageSrc(photo, source))?.height;
}

export function getPreviewImageLoading(index: number): 'eager' | 'lazy' {
  return index === 0 ? 'eager' : 'lazy';
}

export function getPreviewImageFetchPriority(index: number): 'high' | 'low' {
  return index === 0 ? 'high' : 'low';
}

export function getDesktopPreviewImageSizes(index: number, layout: GalleryLayout): string {
  if (layout === 'square-square' && index <= 1) {
    return '(max-width: 767px) 0px, (max-width: 1279px) 50vw, 40vw';
  }

  if (index === 0 || (layout === 'vertical-vertical' && index === 1)) {
    return '(max-width: 767px) 0px, (max-width: 1279px) 60vw, 60vw';
  }

  return '(max-width: 767px) 0px, (max-width: 1279px) 25vw, 20vw';
}

export function getMobilePreviewImageSizes(): string {
  return '(max-width: 767px) calc(100vw - 48px), 0px';
}
